import CheckoutTotalDisplay from "@husky-hrc/Components/CheckoutTotalDisplay";
import ExtendedApplicationState from "@husky-hrc/Framework/Store/ExtendedApplicationState";
import { CheckoutReviewAndSubmitContext } from "@husky-hrc/Overrides/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingFormContext } from "@husky-hrc/Overrides/Pages/CheckoutShippingPage";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrder from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    discountMessage = "discountMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.discountMessage]: string;
    };
}

const mapStateToProps = (state: ExtendedApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = cartId ? getPromotionsDataView(state, cartId).value : getCurrentPromotionsDataView(state).value;
    const { isUpdatingCart } = state.pages.checkoutShipping;
    const currencyCode = state.context.session.currency?.currencyCode;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading || isUpdatingCart,
        currentPage: getCurrentPage(state),
        placeOrderPage: getPageStateByType(state, "CheckoutReviewAndSubmitPage").value,
        placeOrderState: state.placeOrder,
        orderConfirmationPageLink: getPageLinkByPageType(state, "OrderConfirmationPage"),
        currencyCode,
    };
};

const mapDispatchToProps = {
    placeOrder,
    preloadOrderConfirmationData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutShippingCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    cartTotalLabel?: TypographyProps;
    buttonsGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
}

export const cartTotalStyles: CheckoutShippingCartTotalStyles = {
    container: {
        gap: 20,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            background: ${theme.colors.common.background};
                        `,
                        null,
                        null,
                        css`
                            background: #f6f5fb;
                            > div {
                                margin: 0;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartTotalGridItem: {
        width: 12,
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                        height: 53px;
                        background: #f6f5fb;
                    `,
                    css`
                        align-items: center;
                        height: 53px;
                        background: #f6f5fb;
                    `,
                    css`
                        align-items: center;
                        height: 53px;
                        background: #f6f5fb;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                ])}
        `,
    },

    cartTotalLabel: {
        as: "h2",
        css: css`
            &:focus {
                outline: 2px solid ${getProp("theme.focus.color")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            text-transform: uppercase;
                            font-weight: 900;
                            letter-spacing: 2px;
                            line-height: 22px;
                            font-size: 18px;
                        `,
                        null,
                        null,
                        css`
                            width: 90%;
                            font-size: 20px;
                            font-weight: 900;
                            line-height: 24px;
                            letter-spacing: 0.77px;
                            padding: 25px;
                            border-bottom: 1.21px solid #ccccd2;
                            margin-left: 20px;
                            margin-right: 20px;
                            margin-top: 75px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    continueButton: {
        variant: "primary",
        css: css`
            height: 64px;
            border-radius: 4px;
            color: #000000;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        width: 75%;
                    `,
                    css`
                        width: 75%;
                    `,
                ])}
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutShippingCartTotal: FC<Props> = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    currentPage,
    placeOrderPage,
    placeOrderState,
    placeOrder,
    preloadOrderConfirmationData,
    orderConfirmationPageLink,
    history,
    currencyCode,
    fields,
}) => {
    const { showShoppingCartBillingAndShipping } = React.useContext(CheckoutShippingFormContext);
    const { showShoppingCartReviewAndSubmit } = React.useContext(CheckoutReviewAndSubmitContext);
    const onPlaceOrderPage = currentPage === placeOrderPage;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onPlaceOrderPage) {
            placeOrder({
                ...placeOrderState.parameter,
                onSuccess: (cartId: string) => {
                    preloadOrderConfirmationData({
                        cartId,
                        onSuccess: () => {
                            history.push(`${orderConfirmationPageLink!.url}?cartId=${cartId}`);
                        },
                    });
                },
            });
        }
    };

    const shouldShowShoppingCart = () => {
        return onPlaceOrderPage ? showShoppingCartReviewAndSubmit : showShoppingCartBillingAndShipping;
    };

    return (
        <>
            <Hidden above="md">
                {shouldShowShoppingCart() && (
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.cartTotalGridItem}>
                            <Typography data-id="orderSummaryHeading" tabIndex={0} {...styles.cartTotalLabel}>
                                {translate("Order Summary")}
                            </Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <CheckoutTotalDisplay
                                extendedStyles={styles.cartTotal}
                                cart={cart}
                                orderPromotions={orderPromotions}
                                shippingPromotions={shippingPromotions}
                                discountTotal={discountTotal}
                                currencyCode={currencyCode}
                                discountMessage={fields.discountMessage}
                            />
                        </GridItem>
                    </GridContainer>
                )}
            </Hidden>
            <Hidden below="lg">
                <GridContainer {...styles.container}>
                    <GridItem {...styles.cartTotalGridItem}>
                        <Typography {...styles.cartTotalLabel}>{translate("Shopping Cart")}</Typography>
                        <CheckoutTotalDisplay
                            extendedStyles={styles.cartTotal}
                            cart={cart}
                            orderPromotions={orderPromotions}
                            shippingPromotions={shippingPromotions}
                            discountTotal={discountTotal}
                            currencyCode={currencyCode}
                            discountMessage={fields.discountMessage}
                        />
                        <Button
                            {...styles.continueButton}
                            onClick={handleClick}
                            disabled={isLoading}
                            data-test-selector="checkoutShippingCartTotalContinueButton"
                        >
                            {onPlaceOrderPage ? translate("Submit Order") : translate("Review & Submit Your Order")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingCartTotal)),
    definition: {
        group: "Checkout" as any,
        displayName: "Checkout Cart Total",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        fieldDefinitions: [
            {
                name: fields.discountMessage,
                displayName: "Discount Message",
                editorTemplate: "TextField",
                defaultValue: "Congratulations, the special discount agreement with Husky has been applied.",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
